import TPEAction from 'src/models/common/TPEAction';
import CONSTANTS from 'src/utils/constants';
import { DerivedAllocationSheetBuilderState, initialState } from './DerivedAllocationSheetBuilderState';

export const ACTIONS = Object.freeze({
    SET_SHEET_RESULT: new TPEAction('SET_SHEET_RESULT'),
    SET_LOADING: new TPEAction('SET_LOADING'),
    SET_ERROR: new TPEAction('SET_ERROR'),
    SET_VIEW_MODE: new TPEAction('SET_VIEW_MODE'),
    SET_LOCK_VIEW_MODE: new TPEAction('SET_LOCK_VIEW_MODE'),
    SET_DATA_SOURCES: new TPEAction('SET_DATA_SOURCES'),
    SET_TOTALS: new TPEAction('SET_TOTALS'),
    REFRESH_SHEET: new TPEAction('REFRESH_SHEET'),
    REFRESH_TOTALS: new TPEAction('REFRESH_TOTALS'),
    SET_STEP_UNSAVED: new TPEAction('SET_STEP_UNSAVED'),
    SET_EXECUTING: new TPEAction('SET_EXECUTING'),
    EXECUTION_FINISHED: new TPEAction('EXECUTION_FINISHED'),
});

/**
 * Adds `step` to `steps` when `on` is set, else removes it.
 * @returns `steps` itself when nothing changes.
 */
function toggleStep(steps: number[], step: number, on: boolean): number[] {
    if (on === steps.includes(step)) return steps;
    return on ? [...steps, step] : steps.filter((s) => s !== step);
}

export function derivedAllocationSheetBuilderReducer(
    state: DerivedAllocationSheetBuilderState = initialState,
    action: TPEAction,
): DerivedAllocationSheetBuilderState {
    switch (action.type) {
        case ACTIONS.SET_SHEET_RESULT.type: {
            const { sheet, availableVersions, lockInfo } = action.payload ?? {};
            return {
                ...state,
                sheet: sheet ?? null,
                availableVersions: availableVersions ?? [],
                lockInfo: lockInfo ?? null,
                loading: false,
                error: undefined,
            };
        }
        case ACTIONS.SET_LOADING.type:
            return { ...state, loading: action.payload };
        case ACTIONS.SET_ERROR.type:
            return { ...state, error: action.payload, loading: false };
        case ACTIONS.SET_VIEW_MODE.type:
            return { ...state, viewMode: action.payload };
        case ACTIONS.SET_LOCK_VIEW_MODE.type: {
            // Only the draft in DRAFT_IN_PROGRESS can be edited, and not during a run, whatever the lock action reports.
            const draft = state.sheet?.status === 'DRAFT_IN_PROGRESS'
                && state.sheet?.sheetVersion === CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION && !state.executing;
            const editable = action.payload === CONSTANTS.VIEW_MODE.EDITABLE;
            return { ...state, viewMode: editable && !draft ? CONSTANTS.VIEW_MODE.READ_ONLY : action.payload };
        }
        case ACTIONS.SET_DATA_SOURCES.type:
            return { ...state, dataSources: action.payload ?? [] };
        case ACTIONS.SET_TOTALS.type:
            return { ...state, totals: action.payload ?? [] };
        case ACTIONS.REFRESH_SHEET.type:
            return { ...state, sheetRefreshToken: state.sheetRefreshToken + 1 };
        case ACTIONS.REFRESH_TOTALS.type:
            return { ...state, totalsRefreshToken: state.totalsRefreshToken + 1 };
        case ACTIONS.SET_STEP_UNSAVED.type: {
            const { step, unsaved, saving = false } = action.payload as { step: number, unsaved: boolean, saving?: boolean };
            const unsavedSteps = toggleStep(state.unsavedSteps, step, unsaved);
            const savingSteps = toggleStep(state.savingSteps, step, saving);
            return unsavedSteps === state.unsavedSteps && savingSteps === state.savingSteps
                ? state : { ...state, unsavedSteps, savingSteps };
        }
        case ACTIONS.SET_EXECUTING.type:
            return { ...state, executing: action.payload };
        case ACTIONS.EXECUTION_FINISHED.type:
            // The run wrote new values on every step's rows: reread the sheet and its steps.
            return {
                ...state,
                executing: false,
                sheetRefreshToken: state.sheetRefreshToken + 1,
                stepsRevision: state.stepsRevision + 1,
            };
        default:
            return state;
    }
}
